/**
 * Currency Context
 * Manages currency selection and conversion across the application
 * 
 * Exchange rates are fetched from the backend API (which updates daily from exchangerate-api.com)
 * Falls back to cached rates if API is unavailable
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { currencyService, type ExchangeRates } from '../services/currency.service'
import { useOptionalAuth } from './AuthContext'

export type Currency = 'AUD' | 'USD' | 'GBP' | 'EUR' | 'JPY' | 'CNY'

export const SUPPORTED_CURRENCIES: Currency[] = ['AUD', 'USD', 'GBP', 'EUR', 'JPY', 'CNY']

export const isSupportedCurrency = (value: string | null | undefined): value is Currency => {
  if (!value) return false
  return SUPPORTED_CURRENCIES.includes(value.toUpperCase() as Currency)
}

/** What a display shows when an amount cannot be converted honestly. */
export const PRICE_UNAVAILABLE_LABEL = 'Price unavailable'

/**
 * Thrown by `convertPrice` when either currency has no usable rate. Converting a money INPUT
 * (a typed manual price, a sale amount, an import) with a guessed rate would store the wrong
 * amount, so it must fail loudly; displays use `tryConvertPrice` and show "unavailable".
 */
export class MissingExchangeRateError extends Error {
  constructor(currency: string) {
    super(`No exchange rate available for ${currency}`)
    this.name = 'MissingExchangeRateError'
  }
}

/**
 * Pure conversion through USD with explicit failure. Same-currency amounts pass through without a
 * rate. Returns null when a rate is missing — it used to substitute 1, which turned US$10 into
 * A$10 and an A$10 manual price into US$10.
 */
export const convertCentsWithRates = (
  cents: number,
  fromCurrency: string,
  toCurrency: string,
  rates: Partial<Record<string, number>>,
): number | null => {
  if (fromCurrency === toCurrency) return cents
  const rateFor = (currency: string): number | null => {
    if (currency === 'USD') return 1
    const rate = rates[currency]
    return typeof rate === 'number' && Number.isFinite(rate) && rate > 0 ? rate : null
  }
  const fromRate = rateFor(fromCurrency)
  const toRate = rateFor(toCurrency)
  if (fromRate === null || toRate === null) return null
  return Math.round((cents / fromRate) * toRate)
}

interface CurrencyContextType {
  currency: Currency
  /**
   * Change the display currency. Persists to localStorage as the visitor's
   * explicit preference unless `persist: false` — public store pages
   * (scan/basket/store search) adopt the store's currency with
   * `persist: false` so a scan never overwrites the visitor's own choice.
   */
  setCurrency: (currency: Currency, options?: { persist?: boolean }) => void
  /** Formats in the display currency; "Price unavailable" when no rate can convert it. */
  formatPrice: (cents: number, fromCurrency?: Currency) => string
  /**
   * For money INPUT: throws {@link MissingExchangeRateError} rather than guess a rate. Call it
   * from handlers (submit/change), never during render.
   */
  convertPrice: (cents: number, fromCurrency: Currency, toCurrency: Currency) => number
  /** For display: null when a rate is missing, so the caller can show "unavailable". */
  tryConvertPrice: (cents: number, fromCurrency: Currency, toCurrency: Currency) => number | null
  getCurrencySymbol: (currency?: Currency) => string
  baseCurrency: Currency
  isLoading: boolean
  lastUpdated: Date | null
  refreshRates: () => Promise<void>
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined)

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  AUD: 'A$',
  USD: '$',
  GBP: '£',
  EUR: '€',
  JPY: '¥',
  CNY: '¥'
}

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Base currency is always USD (what's stored in the database from JustTCG)
  const baseCurrency: Currency = 'USD'
  
  // Exchange rates state - initialized from localStorage/fallback for immediate use
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>(() => 
    currencyService.getFallbackRates()
  )
  const [isLoading, setIsLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  
  // A preference the visitor saved, if any. Read once: it decides whether the
  // organisation's currency may take over the default below.
  const [savedPreference] = useState<Currency | null>(() => {
    if (typeof window === 'undefined') return null
    try {
      const saved = localStorage.getItem('preferredCurrency')
      return isSupportedCurrency(saved) ? saved.toUpperCase() as Currency : null
    } catch {
      return null
    }
  })
  const [currency, setCurrencyState] = useState<Currency>(() => savedPreference ?? 'AUD')

  // With no saved preference, show money in the signed-in user's store currency.
  // Prices, floors and sale amounts are stored and typed in that currency, so a
  // browser that defaulted to AUD made a USD store read (and enter) every amount
  // converted. An explicit choice — saved, or a public store page adopting its
  // store's currency for this visit — always wins over this default.
  const hasExplicitChoice = useRef(savedPreference !== null)
  const organizationCurrency = useOptionalAuth()?.user?.organizationCurrency?.toUpperCase()
  useEffect(() => {
    if (hasExplicitChoice.current) return
    if (isSupportedCurrency(organizationCurrency)) setCurrencyState(organizationCurrency)
  }, [organizationCurrency])

  // Fetch exchange rates from API on mount
  useEffect(() => {
    let mounted = true
    
    const fetchRates = async () => {
      try {
        const rates = await currencyService.getRates()
        if (mounted) {
          setExchangeRates(rates)
          setLastUpdated(new Date())
        }
      } catch (error) {
        console.warn('Using fallback exchange rates:', error)
      } finally {
        if (mounted) {
          setIsLoading(false)
        }
      }
    }
    
    fetchRates()
    
    // Refresh rates every hour (backend updates daily, but we cache locally)
    const refreshInterval = setInterval(fetchRates, 60 * 60 * 1000)
    
    return () => {
      mounted = false
      clearInterval(refreshInterval)
    }
  }, [])

  // Persistence happens inside setCurrency (not an effect watching `currency`):
  // an effect would write the default value on mount, making it impossible to
  // tell an explicit visitor preference apart from "never chose anything" —
  // which broke store-currency adoption on the public scan/basket pages.
  const setCurrency = useCallback((newCurrency: Currency, options?: { persist?: boolean }) => {
    setCurrencyState(newCurrency)
    hasExplicitChoice.current = true
    if (options?.persist === false) return
    if (typeof window === 'undefined') return
    try {
      localStorage.setItem('preferredCurrency', newCurrency)
    } catch {
      // ignore
    }
  }, [])

  const getCurrencySymbol = useCallback((curr?: Currency): string => {
    return CURRENCY_SYMBOLS[curr || currency]
  }, [currency])

  /**
   * Manually refresh exchange rates from API
   */
  const refreshRates = useCallback(async () => {
    setIsLoading(true)
    try {
      const rates = await currencyService.getRates(true) // Force refresh
      setExchangeRates(rates)
      setLastUpdated(new Date())
    } finally {
      setIsLoading(false)
    }
  }, [])

  const tryConvertPrice = useCallback(
    (cents: number, fromCurrency: Currency, toCurrency: Currency): number | null =>
      convertCentsWithRates(cents, fromCurrency, toCurrency, exchangeRates),
    [exchangeRates],
  )

  const convertPrice = useCallback((cents: number, fromCurrency: Currency, toCurrency: Currency): number => {
    const converted = convertCentsWithRates(cents, fromCurrency, toCurrency, exchangeRates)
    if (converted === null) {
      const missing = convertCentsWithRates(1, fromCurrency, 'USD', exchangeRates) === null ? fromCurrency : toCurrency
      throw new MissingExchangeRateError(missing)
    }
    return converted
  }, [exchangeRates])

  /**
   * Format price in selected currency
   * @param cents - Price in cents (assumed to be in USD/base currency unless specified)
   * @param fromCurrency - The currency the cents value is in (defaults to USD)
   * @returns Formatted price string with currency symbol
   */
  const formatPrice = useCallback((cents: number, fromCurrency: Currency = 'USD'): string => {
    // Convert to the selected display currency
    const convertedCents = tryConvertPrice(cents, fromCurrency, currency)
    if (convertedCents === null) return PRICE_UNAVAILABLE_LABEL

    const symbol = getCurrencySymbol(currency)
    const amount = (convertedCents / 100).toFixed(2)
    
    return `${symbol}${amount}`
  }, [currency, tryConvertPrice, getCurrencySymbol])

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<CurrencyContextType>(() => ({
    currency,
    setCurrency,
    formatPrice,
    convertPrice,
    tryConvertPrice,
    getCurrencySymbol,
    baseCurrency,
    isLoading,
    lastUpdated,
    refreshRates
  }), [currency, setCurrency, formatPrice, convertPrice, tryConvertPrice, getCurrencySymbol, baseCurrency, isLoading, lastUpdated, refreshRates])

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  )
}

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext)
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider')
  }
  return context
}

export default CurrencyContext
